import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import { postCTOSweep } from "../../api/accountsAPI";
import { HandleGridBody } from "../../components/common/HandleGridBody";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import SearchComboBox from "../../components/common/SearchComboBox";
import CONSTANT from "CONSTANT/Matter/ComboxType";
import { AllocationCell, SelectCell, MatterListItem } from "./CustomComponents";
import { Label } from "@progress/kendo-react-labels";
import { TransactionPeriod } from "components/common/TransactionPeriod";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { getOfficeCreditMatterList } from "services/postingsService";
import { arrowRotateCwIcon as refreshIcon } from "@progress/kendo-svg-icons";
import { toast } from "react-toastify";
import OfficeCreditGrid from "pages/OfficeCreditAndAllocations/OfficeCreditAndAllocationGrid";
import * as api from "../../api/index";
import { AmountFormatCell, AmountFormat } from "components/common/formatter";
import styles from "./CtoSweep.module.scss";
import { GRID_PAGE_SIZES } from "components/common/GridComponents";
import { changeLedgerLink } from "actions/ledgerLinks.action";
import { useNavigate } from "react-router-dom";

const CTO_SWEEP_PAGE_SIZE = 100;

const CtoSweep = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [ctoState, setCtoState] = useState({
        date: new Date(),
        clientBank: null,
        officeBank: null,
        autoAllocation: true,
        forBillsOnly: false,
        includeRingfenced: false,
        transactionPeriod: null,
        allocation: null,
        showAllocationModal: false,
        postedMatters: [],
        selectedMatterId: null,
        selectedMatter: null,
    });
    const [dataState, setDataState] = useState({
        take: CTO_SWEEP_PAGE_SIZE,
        skip: 0,
        sort: [
            {
                field: "matterRef",
                dir: "asc",
            },
        ],
    });
    const [gridData, setGridData] = useState({
        data: [],
        total: 0,
    });
    const [reload, setReload] = useState();
    const [allocationList, setAllocationList] = useState([]);
    const isPostRefresh = React.useRef(false);

    // Calculate selected rows count and total allocation amount
    const { selectedCount, totalAllocation } = useMemo(() => {
        const selectedRows = gridData.data.filter((item) => item.add);
        return {
            selectedCount: selectedRows.length,
            totalAllocation: selectedRows.reduce(
                (sum, item) => sum + (Number(item.maxAllocateAmount) || 0),
                0,
            ),
        };
    }, [gridData.data]);

    const NullCell = () => null;

    // Footer cell for showing selected count
    // Footer cell for showing selected count
    const SelectFooterCell = () => {
        return (
            <td colSpan={2} className="text-end fw-semibold"  >
                {selectedCount > 0 && (
                    <>{selectedCount} posting{selectedCount !== 1 ? "s" : ""} selected</>
                )}
            </td>
        );
    };

    // Footer cell for showing total allocation
    const AllocationFooterCell = () => {
        return (
            <td className="text-end fw-semibold">{AmountFormat(totalAllocation)}</td>
        );
    };

    const createState = (skip, take) => {
        let pagerSettings = {
            pageSizes: GRID_PAGE_SIZES,
        };
        return {
            pageSize: take,
            pageable: pagerSettings,
        };
    };

    const [state, setState] = useState(createState(0, 10));

    const onDataStateChange = (e) => {
        if (
            e.dataState.sort !== undefined &&
            e.dataState.skip !== 0 &&
            dataState.sort !== e.dataState.sort
        ) {
            setDataState({ ...e.dataState, skip: 0 });
        } else if (e.dataState?.sort?.[0] === undefined) {
            setDataState({
                ...e.dataState,
                sort: [
                    {
                        field: dataState?.sort?.[0].field,
                        dir: "asc",
                    },
                ],
            });
        } else {
            setDataState(e.dataState);
        }
    };

    const handleStateChange = (key, value) => {
        if (typeof key === "object" && key !== null) {
            setCtoState((prev) => ({ ...prev, ...key }));
        } else {
            setCtoState((prev) => ({ ...prev, [key]: value }));
        }
    };

    const updateOfficeCreditAllocations = useCallback((value) => {
        setCtoState((prev) => ({ ...prev, allocation: value }));
    }, []);

    const handleItemChange = (event) => {
        const updatedData = gridData.data.map((item) =>
            item.matterId === event.dataItem.matterId
                ? { ...item, [event.field]: event.value }
                : item,
        );

        setGridData({
            ...gridData,
            data: updatedData,
        });
    };

    const handlePost = async () => {
        const ctoPostingInfo = gridData.data
            .filter((item) => item.add)
            .map((item) => ({
                matterId: item.matterId,
                allocateAmount: Number(item.maxAllocateAmount || 0),
            }));

        if (ctoPostingInfo.length === 0) {
            toast.warn("No items selected for posting.");
            return;
        }

        const body = {
            date: ctoState.date?.toISOString() || new Date().toISOString(),
            clientBank: ctoState.clientBank ? ctoState.clientBank.nominalRef : null,
            officeBank: ctoState.officeBank ? ctoState.officeBank.nominalRef : null,
            transactionPeriod: ctoState.transactionPeriod?.period || 0,
            autoAllocate: ctoState.autoAllocation,
            billsOnly: ctoState.forBillsOnly,
            includeRingfenced: ctoState.includeRingfenced,
            ctoPostingInfo,
        };

        try {
            const response = await postCTOSweep(body);
            const responseData = response.data?.data || [];
            const unallocatedMatters = responseData.filter((item) => !item.allocated);

            if (unallocatedMatters.length > 0) {
                handleStateChange({
                    postedMatters: unallocatedMatters,
                    selectedMatter: unallocatedMatters[0],
                    selectedMatterId: unallocatedMatters[0].matterId,
                    showAllocationModal: true,
                });
            } else {
                toast.success("CTO Sweep posted successfully");
            }

            // Mark this as a post refresh (don't select all)
            isPostRefresh.current = true;
            setReload((prev) => !prev);
        } catch (err) { }
    };

    const handleDataReceived = (data) => {
        if (data.data) {
            const updatedData = data.data.map((item) => ({
                ...item,
                add: !isPostRefresh.current, // Select all except after posting
                originalMaxAllocateAmount: item.maxAllocateAmount,
                allocateAmount: item.allocateAmount || 0,
            }));

            setGridData({
                data: updatedData,
                total: data.total || gridData.total,
            });

            // Reset the flag after data is received
            isPostRefresh.current = false;
        }
    };

    useEffect(() => {
        const loadAllocationList = async () => {
            if (ctoState.selectedMatterId) {
                try {
                    const list = await getOfficeCreditMatterList(ctoState.selectedMatterId);
                    setAllocationList(list);
                } catch (error) {
                    // Error already handled by API
                }
            }
        };

        loadAllocationList();

        return () => {
            setAllocationList([]);
        };
    }, [ctoState.selectedMatterId]);

    const handleSubmit = useCallback(async () => {
        const postingData = {
            postingType: "CTO",
            matterID: ctoState.selectedMatterId,
            feeEarnerRef: allocationList[0]?.feeEarnerRef || "MK1",
            netAmount: ctoState.selectedMatter?.amount,
            transactionPeriod: ctoState.transactionPeriod?.period,
            currencyCode: "GBP",
            details: ctoState.selectedMatter?.details || "No Details Available",
            reference: "DUMMYREF",
            date: ctoState.date?.toISOString() || new Date().toISOString(),
            clientBank: ctoState.clientBank?.nominalRef || null,
            officeBank: ctoState.officeBank?.nominalRef || null,
            allocations: ctoState.allocation,
        };

        try {
            const response = await api.createposting([postingData]);
            toast.success(
                `Posted Successfully for ${response?.data?.data[0]?.postingType} - ${response?.data?.data[0]?.transactionNo}`,
            );

            handleStateChange("showAllocationModal", false);
            handleStateChange("postedMatters", []);
            handleStateChange("selectedMatterId", null);
            handleStateChange("selectedMatter", null);
            handleStateChange("allocation", null);
            setAllocationList([]);
            setReload(true);
        } catch (error) { }
    }, [ctoState, allocationList]);

    const handleMatterSelection = useCallback((matter) => {
        handleStateChange({
            selectedMatterId: matter.matterId,
            selectedMatter: matter,
        });
    }, []);

    const handleMatterLedgerOpen = useCallback(
        (matter) => {
            if (!matter?.matterId) return;
            dispatch(changeLedgerLink("MatterLedger", matter));
            navigate("/accounts/matterledger");
        },
        [dispatch, navigate],
    );

    const handleRefresh = () => {
        // Manual refresh - select all (isPostRefresh stays false)
        setReload((prev) => !prev);
    };

    const MatterRefCell = (props) => {
        return (
            <td>
                <span
                    style={{ cursor: "pointer" }}
                    onClick={() => handleMatterLedgerOpen(props.dataItem)}>
                    <u>{props.dataItem.matterRef}</u>
                </span>
            </td>
        );
    };

    // Check if all rows are selected
    const allSelected = useMemo(() => {
        return gridData.data.length > 0 && gridData.data.every((item) => item.add);
    }, [gridData.data]);

    // Handle select all / select none
    const handleSelectAll = (e) => {
        const updatedData = gridData.data.map((item) => ({
            ...item,
            add: e.value,
        }));
        setGridData({
            ...gridData,
            data: updatedData,
        });
    };

    // Header cell with checkbox for select all
    const SelectHeaderCell = () => {
        return (
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
                Select
                <Checkbox
                    checked={allSelected}
                    onChange={handleSelectAll}
                />
            </span>
        );
    };

    return (
        <div className="h-100">
            <TransactionPeriod
                onRefreshTransactionPeriod={(period) =>
                    handleStateChange("transactionPeriod", period)
                }
            />

            <div
                className="page d-flex flex-column"
                style={{ height: "calc(100% - 40px)" }}>
                <div className="d-flex align-items-center mb-3 gap-4 flex-wrap">
                    <div className="d-flex flex-column " style={{ width: 215 }}>
                        <Label className="mb-1">Date</Label>
                        <DatePicker
                            value={ctoState.date}
                            onChange={(e) => handleStateChange("date", e.value)}
                            format="dd/MM/yyyy"
                        />
                    </div>

                    <div className="d-flex flex-column " style={{ width: 215 }}>
                        <SearchComboBox
                            name="clientBank"
                            cbType={CONSTANT.CB}
                            width="100%"
                            boxShadow={true}
                            className="mt-1"
                            customCombo={true}
                            placeholder={"Select Client Bank"}
                            value={ctoState.clientBank}
                            onChangeFunction={(e) => handleStateChange("clientBank", e.value)}
                        />
                    </div>

                    <div className="d-flex flex-column " style={{ width: 215 }}>
                        <SearchComboBox
                            name="officeBank"
                            cbType={CONSTANT.OB}
                            width="100%"
                            boxShadow={true}
                            className="mt-1"
                            placeholder={"Select Office Bank"}
                            customCombo={true}
                            value={ctoState.officeBank}
                            onChangeFunction={(e) => handleStateChange("officeBank", e.value)}
                        />
                    </div>
                    <div className="mt-3 d-flex gap-3 flex-wrap">
                        <Checkbox
                            checked={ctoState.autoAllocation}
                            onChange={(e) => handleStateChange("autoAllocation", e.value)}
                            label={<span className="fs-md fw-semibold ">Auto Allocation</span>}
                            className="align-self-center"
                        />

                        <Checkbox
                            checked={ctoState.forBillsOnly}
                            onChange={(e) => handleStateChange("forBillsOnly", e.value)}
                            label={<span className="fs-md fw-semibold">For Bills Only</span>}
                            className="align-self-center"
                        />

                        {/*<Checkbox*/}
                        {/*    checked={ctoState.includeRingfenced}*/}
                        {/*    onChange={(e) => handleStateChange("includeRingfenced", e.value)}*/}
                        {/*    label={*/}
                        {/*        <span className="fs-md fw-semibold">Include Ring-fenced</span>*/}
                        {/*    }*/}
                        {/*    className="align-self-center"*/}
                        {/*/>*/}
                    </div>

                    <Button
                        className="common-btn ms-auto mt-3"
                        svgIcon={refreshIcon}
                        onClick={handleRefresh}
                    >
                        Refresh
                    </Button>
                </div>

                <Grid
                    {...dataState}
                    data={gridData}
                    pageable={state.pageable}
                    pageSize={state.pageSize}
                    onDataStateChange={onDataStateChange}
                    onItemChange={handleItemChange}
                    sortable
                    dataItemKey="matterId"
                    editable={true}
                    className="fs-md flex-grow-1 overflow-auto grid-minwidth-col2-200">
                    <Column
                        field="matterRef"
                        title="Matter Ref"
                        editable={false}
                        width="140px"
                        cell={MatterRefCell}
                    />
                    <Column field="matterDetails" title="Matter Details" editable={false} />
                    <Column
                        field="clientBalance"
                        title="Client Balance"
                        editable={false}
                        width="140px"
                        cell={AmountFormatCell}
                        headerClassName="grid-header-center"
                    />
                    <Column
                        field="ringfencedAmount"
                        title="Ring-fenced"
                        editable={false}
                        width="140px"
                        cell={AmountFormatCell}
                        headerClassName="grid-header-center"
                    />
                    <Column
                        field="officeBalance"
                        title="Office Balance"
                        editable={false}
                        width="140px"
                        cell={AmountFormatCell}
                        headerClassName="grid-header-center"
                    />
                    <Column
                        field="billBalance"
                        title="Bills Due"
                        editable={false}
                        width="140px"
                        cell={AmountFormatCell}
                        headerClassName="grid-header-center"
                        footerCell={NullCell}
                    />
                    <Column
                        field="add"
                        title="Select"
                        cell={SelectCell}
                        width="100px"
                        headerCell={SelectHeaderCell}
                        footerCell={SelectFooterCell}
                        sortable={false}
                    />
                    <Column
                        field="maxAllocateAmount"
                        title="Allocation"
                        cell={AllocationCell}
                        width="140px"
                        headerClassName="grid-header-center"
                        footerCell={AllocationFooterCell}
                        sortable={false}
                    />
                </Grid>
                <HandleGridBody
                    dataState={dataState}
                    onDataReceived={handleDataReceived}
                    currentComponent="ctoSweep"
                    refresh={reload}
                    parameter={{
                        billsOnly: ctoState.forBillsOnly,
                        includeRingfenced: ctoState.includeRingfenced,
                    }}
                />
                <div className="d-flex justify-content-end mt-2">
                    <Button className="common-btn" onClick={handlePost}>
                        Post
                    </Button>
                </div>

                {ctoState.showAllocationModal && (
                    <Dialog
                        title="CTO Sweep Allocation"
                        onClose={() => handleStateChange("showAllocationModal", false)}
                        className={`${styles.dialog_container} dialog-xxl`}>
                        <div className="d-flex h-100">
                            <div className={`${styles.matterlist_container}`}>
                                {ctoState.postedMatters.map((matter) => (
                                    <MatterListItem
                                        key={matter.matterId}
                                        matter={matter}
                                        isSelected={matter.matterId === ctoState.selectedMatterId}
                                        onClick={() => handleMatterSelection(matter)}
                                    />
                                ))}
                            </div>

                            <div className={`${styles.grid_container}`}>
                                {ctoState.selectedMatterId && (
                                    <OfficeCreditGrid
                                        className={`${styles.grid_customstyle}  grid-minwidth-col4-150`}
                                        officeCreditList={allocationList}
                                        updateAllocations={updateOfficeCreditAllocations}
                                    />
                                )}
                            </div>
                        </div>

                        <DialogActionsBar>
                            <div className="dialog-footer w-100">
                                <Button className="common-btn me-2" onClick={handleSubmit}>
                                    Post
                                </Button>
                                <Button
                                    className="common-btn bgBtnSecondary me-2"
                                    onClick={() => handleStateChange("showAllocationModal", false)}>
                                    Close
                                </Button>
                            </div>
                        </DialogActionsBar>
                    </Dialog>
                )}
            </div>
        </div>
    );
};

export default CtoSweep;
